import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import { Grid, Text } from 'elements';
import moment, { Moment } from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  Cell,
  Label,
} from 'recharts';
import {
  getDashboardStatistics,
  getDashboardCompanyStatistics,
  getDashboardMentorStatistics,
  patchInterviewDate,
  getMyAverageScore,
  getPassedAverageScore,
} from 'libs/api/mentoring';
import { TFeedback, TCompanyFeedback, TMentorFeedbackData } from 'modules/mentoring/types';
import { CWindowWidth, CDateFormat } from 'constants/base.const';
import { TcompanyColor, companyColor, feedbackType } from 'constants/mentoring.const';
import { Grid as AGrid, Button, Select, DatePicker, Spin, Col } from 'antd';
import { ConsoleSqlOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Colors, SecondaryColor, PrimaryColor, device } from 'GlobalStyles';
import DefaultUser from 'assets/icons/default.webp';

type TLegends = {
  // '전체 평균'?: { color: string; isFocus: boolean };
  // '합격자 평균'?: { color: string; isFocus: boolean };
  '내 점수': { color: string; isFocus: boolean };
};

type TScoreAverage = {
  name: string;
  score: number;
  short_name?: string;
};

const Dashboard = () => {
  const mentorDataLimit = 5;
  const ref = useRef<HTMLLinkElement>(null);
  const screens = AGrid.useBreakpoint();
  const history = useHistory();
  const [scopeType, setScopeType] = useState('overall_score');
  const [slider, setSlider] = useState<Slider | null>();
  const [legends, setLegends] = useState<TLegends>({
    // '전체 평균': { color: '#D81F26', isFocus: false },
    // '합격자 평균': { color: '#3CAC40', isFocus: false },
    '내 점수': { color: '#003159', isFocus: true },
  });
  const [feedbackData, setFeedbackData] = useState<Array<TFeedback>>();
  const [myScoreAverage, setMyScoreAverage] = useState<Array<TScoreAverage>>();
  const [passedScoreAverage, setPassedScoreAverage] = useState<Array<TScoreAverage>>();
  const [companyData, setCompanyData] = useState<Array<TCompanyFeedback>>();
  const [mentorPage, setMentorPage] = useState(1);
  const [mentorData, setMentorData] = useState<Array<TMentorFeedbackData>>();
  const [mentorDataTotal, setMentorDataTotal] = useState(0);
  const [height, setHeight] = useState(0);
  const [interview, setInterview] = useState<Moment>();

  const ticks = [
    'Bad',
    screens.lg ? 'Need Improvement' : 'Need Improve -ment',
    'Average',
    'Good',
    'Excellent',
  ];

  const getStatistics = async () => {
    const { success, data } = await getDashboardStatistics({
      score_type: scopeType,
    });
    if (success) {
      const formatTime = data.map((el: { [key: string]: string | number }) => {
        const formattedTime = moment(el.name).format('M월D일');
        return { ...el, name: formattedTime };
      });
      setFeedbackData(formatTime);
    }
  };

  const formatData = (getData: Array<{ name: string; score: number }>) => {
    const formattedData = getData.map((el: TScoreAverage) => {
      const decimalRounding = Number(el.score?.toFixed(1));
      return {
        ...el,
        short_name: el.name.slice(0, 1),
        score: decimalRounding,
      };
    });
    return formattedData;
  };

  const getMyAverageScoreStatistics = async () => {
    const { success, data } = await getMyAverageScore();
    if (success) {
      const formatNaNScore = formatData(data).map(
        (el: { name: string; score: number; short_name: string }) => {
          if (!el.score) {
            return { ...el, score: 0 };
          }
          return { ...el };
        },
      );
      setMyScoreAverage(formatNaNScore);
    }
  };

  const getPassedAverageScoreStatistics = async () => {
    const { success, data } = await getPassedAverageScore();
    if (success) {
      setPassedScoreAverage(formatData(data));
    }
  };

  const getCompanyStatistics = async () => {
    const randomColor = () => {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (var i = 0; i < 6; i += 1) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    };
    const { success, data } = await getDashboardCompanyStatistics();
    if (success) {
      setCompanyData(
        Object.keys(data).map((key: string) => ({
          company_name: key,
          color: companyColor[key as keyof TcompanyColor] || randomColor(),
          rating_count: data[key]?.length > 0 ? data[key][0].mentor_numbers : 0,
          data: data[key].map((d: any) => {
            d.feedback = `${d.feedback.slice(0, 4)}\n(${Number(d.score)?.toFixed(1)})`;
            d.score = Number(Number(d.score)?.toFixed(1));
            return d;
          }),
        })),
      );
    }
  };
  const getMentorStatistics = async () => {
    const { success, data } = await getDashboardMentorStatistics({
      current: mentorPage,
      pageSize: mentorDataLimit,
    });
    if (success) {
      if (mentorData && mentorData.length > 0) {
        setMentorData(mentorData.concat(data.contents));
      } else {
        setMentorData(data.contents);
        setMentorDataTotal(data.total);
      }
    }
  };

  const yTickFormatter = (tick: number) => {
    return ticks[tick - 1] || '';
  };

  //면접 예정일 날짜 선택 삭제
  // const selectMeetingDate = async (date: any, dateString: string) => {
  //   setInterview(date);
  //   const { success } = await patchInterviewDate(dateString);
  //   if (success) getStatistics();
  // };

  //범례 선택 삭제
  // const legendEnter = (tf: boolean, key: keyof TLegends) => {
  //   setLegends(legends => {
  //     legends[key].isFocus = tf;
  //     return { ...legends };
  //   });
  // };

  // const legendContent = (props: any) => {
  //   const { payload } = props;
  //   return (
  //     <Grid marginTop="50px" sm={{ marginTop: '20px' }}>
  //       <Grid justify="flex-end" gap="20px">
  //         {payload.map(
  //           (
  //             {
  //               color,
  //               value,
  //               dataKey
  //             }: { value: string; color: string; dataKey: keyof TLegends },
  //             index: number
  //           ) => (
  //             <Grid
  //               key={index}
  //               isInline
  //               padding="4px 8px"
  //               border={`1px solid ${
  //                 legends[dataKey].isFocus ? color : `${color}77`
  //               }`}
  //               borderRadius="8px"
  //               cursor="pointer"
  //               onMouseEnter={() => legendEnter(true, dataKey)}
  //               onMouseLeave={() => legendEnter(false, dataKey)}
  //             >
  //               <Text
  //                 size="1.2rem"
  //                 bold
  //                 color={legends[dataKey].isFocus ? color : `${color}77`}
  //               >
  //                 {value}
  //               </Text>
  //             </Grid>
  //           )
  //         )}
  //       </Grid>
  //     </Grid>
  //   );
  // };

  const calcTick = (company: TCompanyFeedback) => (props: any) => {
    const payload = { ...props.payload };
    return (
      <text {...props}>
        {payload.value.split('\n').map((v: string, index: number) => (
          <tspan
            x={props.x}
            dy={index > 0 ? '1.2rem' : '0'}
            style={{
              fontSize: '0.9rem',
              fill: index !== 0 ? company.color : undefined,
            }}
          >
            {v}
          </tspan>
        ))}
      </text>
    );
  };

  useEffect(() => {
    getMentorStatistics();
  }, [mentorPage]);

  useEffect(() => {
    if (companyData && ref.current !== null) {
      setHeight(ref.current.offsetWidth || 0);
    }
  }, [companyData]);

  useEffect(() => {
    getStatistics();
    getMyAverageScoreStatistics();
    getPassedAverageScoreStatistics();
  }, [scopeType]);

  useEffect(() => {
    getCompanyStatistics();
  }, []);

  //범례(legend)의 포커스 여부에 따라 isFocus boolean 값이 변한다
  // console.log(
  //   'legends',
  //   legends &&
  //     Object.keys(legends).map(
  //       (key: string) => legends[key as keyof TLegends].isFocus
  //     )
  // );

  const CustomTooltip = ({ active, payload, label, isMine }: any) => {
    if (active && payload && payload.length) {
      let fullLabel;
      if (label === 'C') {
        fullLabel = 'Comm';
      } else if (label === 'P') {
        fullLabel = 'PS';
      } else if (label === 'I') {
        fullLabel = 'Impact';
      } else if (label === 'O') {
        fullLabel = 'Overall';
      } else {
        fullLabel = label;
      }
      return (
        <div
          className='custom-tooltip'
          style={{
            margin: '0px',
            padding: '10px 10px 0 10px',
            backgroundColor: 'rgb(255, 255, 255)',
            border: '1px solid rgb(204, 204, 204)',
            whiteSpace: 'nowrap',
            lineHeight: 0.8,
          }}
        >
          <p>{`${fullLabel}`}</p>
          {isMine ? (
            <p style={{ color: '#003159' }}>
              {payload[0].value === 0 ? `나의 피드백 평균 : -` : `나의 피드백 평균 : ${payload[0].value}`}
            </p>
          ) : (
            <p style={{ color: '#3cac40' }}>{`합격자 피드백 평균: ${payload[0].value}`}</p>
          )}
        </div>
      );
    }

    return null;
  };

  const graphWidth = () => {
    if (feedbackData) {
      if (screens.lg) {
        return feedbackData.length * 200;
      } else if (screens.md) {
        return feedbackData.length * 130;
      } else {
        return feedbackData.length * 70;
      }
    }
  };

  return (
    <DashboardWrapper>
      <Grid padding='40px 0 150px 0' justify='center'>
        <Grid
          isInline
          width='100%'
          maxWidth={`${CWindowWidth}px`}
          padding='0 50px'
          sm={{ padding: '0 20px' }}
        >
          <Grid>
            <Text bold size='2.5rem'>
              멘토링 통계
            </Text>
          </Grid>
          <Grid marginTop='32px'>
            <Text bold size='1.3rem'>
              피드백 시계열 차트
            </Text>
          </Grid>
          <Grid marginTop='5px'>
            <Text>
              {/* 나의 진척도를 한눈에 확인해 보세요. Career Step 멘티 전체 및 컨설팅
            합격자 평균과 비교할 수 있습니다. (면접 예정일 기준 최근 6개월까지) */}
            </Text>
          </Grid>
          <Grid marginTop='42px' border={`1px solid ${Colors.grey1}`} padding='20px' sm={{ padding: '10px' }}>
            <Grid>
              <Grid justify='flex-end' gap='16px' align='center'>
                {/* <Grid isInline sm={{ display: 'none' }}>
                  <Text size="1.1rem">면접 예정일</Text>
                  </Grid>
                  <DatePicker
                    showTime
                    format={CDateFormat}
                    placeholder="면접 예정일"
                    style={{ backgroundColor: '#f0f8f6', border: 0, borderRadius: 8 }}
                    value={interview}
                    onChange={selectMeetingDate}
                  /> */}
                <Select
                  value={scopeType}
                  placeholder='피드백 지표'
                  onChange={setScopeType}
                  style={{ width: 150 }}
                >
                  <Select.Option value={'overall_score'}>Overall</Select.Option>
                  <Select.Option value={'impact_score'}>Impact</Select.Option>
                  <Select.Option value={'problemsolving_score'}>Problem Solving</Select.Option>
                  <Select.Option value={'communication_score'}>Communication</Select.Option>
                </Select>
              </Grid>
              <Grid height='500px' width='100%' marginTop='10px' position='relative'>
                {!feedbackData?.length && (
                  <Grid justify='center' margin='auto 0' position='absolute' left='5%' top='45%'>
                    <Text color={Colors.grey2} size='1.2rem'>{`${
                      feedbackType[scopeType.split('_')[0]]
                    } 점수가 없습니다.`}</Text>
                  </Grid>
                )}
                {/* <div style={{ overflowX: 'scroll' }}> */}
                {feedbackData && feedbackData.length >= 5 && (
                  <div
                    style={{
                      display: 'flex',
                      overflowX: 'auto',
                      flexDirection: 'row-reverse',
                    }}
                  >
                    <ResponsiveContainer width={graphWidth()} height='100%'>
                      <LineChart
                        // width={2000}
                        height={500}
                        data={feedbackData}
                      >
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' axisLine={false} tickMargin={15}></XAxis>
                        <YAxis
                          type='number'
                          ticks={[1, 2, 3, 4, 5]}
                          tickMargin={15}
                          tickCount={10}
                          tickFormatter={yTickFormatter}
                          axisLine={false}
                          padding={{ top: 20, bottom: 20 }}
                          width={screens.lg ? 150 : 73}
                        />
                        <Tooltip />
                        {/* <Legend content={legendContent} /> */}
                        {Object.keys(legends).map((key: string) => (
                          <Line
                            key={key}
                            type='linear'
                            dataKey={key}
                            name={key}
                            stroke={legends[key as keyof TLegends].color}
                            strokeWidth={legends[key as keyof TLegends].isFocus ? 2 : 1}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {feedbackData && feedbackData.length < 5 && (
                  <ResponsiveContainer width='100%' height='100%'>
                    <LineChart
                      // width={2000}
                      height={500}
                      data={feedbackData}
                    >
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis dataKey='name' axisLine={false} tickMargin={15}></XAxis>
                      <YAxis
                        type='number'
                        ticks={[1, 2, 3, 4, 5]}
                        tickMargin={15}
                        tickCount={10}
                        tickFormatter={yTickFormatter}
                        axisLine={false}
                        padding={{ top: 20, bottom: 20 }}
                        width={screens.lg ? 150 : 73}
                      />
                      <Tooltip />
                      {/* <Legend content={legendContent} /> */}
                      {Object.keys(legends).map((key: string) => (
                        <Line
                          key={key}
                          type='linear'
                          dataKey={key}
                          name={key}
                          stroke={legends[key as keyof TLegends].color}
                          strokeWidth={legends[key as keyof TLegends].isFocus ? 2 : 1}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid
            marginTop='50px'
            style={{ borderTop: `1px solid ${Colors.grey1}` }}
            sm={{ display: 'none' }}
          />

          <Grid marginTop='32px'>
            <Text bold size='1.3rem'>
              합격자와 비교하기
            </Text>
          </Grid>
          <Grid marginTop='5px'>
            <Text>
              최근 멘토링 3회의 평균입니다. Impact: 회사별 Fit, PS: Problem Solving, Comm: Communication 을
              의미합니다.
            </Text>
          </Grid>
          <Grid marginTop='42px' border={`1px solid ${Colors.grey1}`} padding='0px' sm={{ padding: '10px' }}>
            <Grid
              height='500px'
              marginTop='10px'
              style={{ display: 'flex', justifyContent: 'space-around' }}
              md={{ justifyContent: 'center' }}
            >
              <Grid style={{ width: '45%', height: '95%' }} md={{ width: '48%' }}>
                <Text bold size='1.3rem' color={PrimaryColor} xs={{ size: '1.1rem' }}>
                  나의 피드백 평균
                </Text>

                <ResponsiveContainer>
                  <BarChart height={400} data={myScoreAverage}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey={screens.md ? 'name' : 'short_name'}
                      // dataKey={'name'}
                      axisLine={false}
                      padding={screens.xl ? { left: 10, right: 10 } : { left: 0, right: 0 }}
                      tickMargin={1}
                    />
                    <YAxis
                      type='number'
                      ticks={[1, 2, 3, 4, 5]}
                      tickMargin={15}
                      tickCount={10}
                      tickFormatter={yTickFormatter}
                      axisLine={false}
                      // padding={{ top: 20, bottom: 20 }}
                      width={screens.lg ? 150 : 73}
                    />

                    <Tooltip content={<CustomTooltip isMine={true} />} wrapperStyle={{ left: -50 }} />
                    <Bar dataKey={'score'} name={'나의 피드백 평균'} fill={'#003159'} barSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
              <Grid style={{ width: '45%', height: '95%' }} md={{ width: '48%' }}>
                <Text bold size='1.3rem' color={SecondaryColor} xs={{ size: '1.1rem' }}>
                  합격자 피드백 평균
                </Text>
                <ResponsiveContainer>
                  <BarChart height={400} data={passedScoreAverage}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey={screens.md ? 'name' : 'short_name'}
                      // dataKey={'name'}
                      axisLine={false}
                      padding={screens.xl ? { left: 10, right: 10 } : { left: 0, right: 0 }}
                      tickMargin={1}
                    />
                    <YAxis
                      type='number'
                      ticks={[1, 2, 3, 4, 5]}
                      tickMargin={15}
                      tickCount={10}
                      tickFormatter={yTickFormatter}
                      axisLine={false}
                      width={screens.lg ? 150 : 73}
                    />
                    <Tooltip content={<CustomTooltip isMine={false} />} wrapperStyle={{ left: -50 }} />

                    <Bar dataKey={'score'} name={'합격자 피드백 평균'} fill={'#3CAC40'} barSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            marginTop='50px'
            style={{ borderTop: `1px solid ${Colors.grey1}` }}
            sm={{ display: 'none' }}
          />

          <Grid marginTop='50px'>
            <Text bold size='1.3rem'>
              멘토 소속 회사별 피드백 통계{' '}
            </Text>
          </Grid>
          <Grid marginTop='5px'>
            <Text>
              Excellent: 5점 ~ Bad: 1점으로 환산된 점수입니다.Impact: 회사별 Fit, PS: Problem Solving, Comm:
              Communication 을 의미합니다.
            </Text>
          </Grid>
          <Grid justify='space-between' align='center' marginTop='30px' gap='2%' sm={{ display: 'none' }}>
            {!!companyData &&
              companyData.map((company, index) => (
                <Grid ref={index === 0 ? ref : undefined} key={index} isInline align='center' width='32%'>
                  <Grid padding='0 40px' gap='10px' align='center'>
                    <Grid
                      isInline
                      padding='4px 8px'
                      border={`1px solid ${company.color}`}
                      borderRadius='8px'
                      width='100px'
                      justify='center'
                    >
                      <Text size='0.9rem' bold color={company.color}>
                        {company.company_name}
                      </Text>
                    </Grid>
                    <Text color={Colors.grey4}>평가 멘토수: {company.rating_count}</Text>
                  </Grid>
                  <Grid height={`${height}px`} marginTop='10px' justify='center'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <RadarChart outerRadius='60%' data={company.data}>
                        {/* 가장 바깥 다이아몬드 line */}
                        <PolarGrid />
                        <PolarAngleAxis dataKey='feedback' tick={calcTick(company)} />
                        {/* 각 영역의 카테고리이름 및 점수 */}
                        <PolarRadiusAxis domain={[0, 5]} />
                        <Radar
                          name='Mike'
                          dataKey='score'
                          stroke={company.color}
                          fill={company.color}
                          fillOpacity={0.6}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              ))}
          </Grid>
          <Grid display='none' marginTop='30px' sm={{ display: 'block' }}>
            <Grid marginTop='8px' gap='8px'>
              <Button type='text' icon={<LeftOutlined />} onClick={() => slider?.slickPrev()} />
              <Button type='text' icon={<RightOutlined />} onClick={() => slider?.slickNext()} />
            </Grid>
            <Grid display='block' marginTop='20px'>
              <Slider ref={setSlider} dots={false} arrows={false}>
                {!!companyData &&
                  companyData.map((company, index) => (
                    <Grid align='center' key={index}>
                      <Grid padding='0 20px' gap='10px' align='center'>
                        <Grid
                          isInline
                          padding='4px 8px'
                          border={`1px solid ${company.color}`}
                          borderRadius='8px'
                          width='100px'
                          justify='center'
                        >
                          <Text size='0.9rem' bold color={company.color}>
                            {company.company_name}
                          </Text>
                        </Grid>
                        <Text color={Colors.grey4}>평가 멘토수: {company.rating_count}</Text>
                      </Grid>
                      <Grid height='300px' marginTop='10px'>
                        <ResponsiveContainer width='100%' height='100%'>
                          <RadarChart outerRadius='80%' data={company.data}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey='feedback' tick={calcTick(company)} />
                            <PolarRadiusAxis domain={[0, 5]} />
                            <Radar
                              name='Mike'
                              dataKey='score'
                              stroke={company.color}
                              fill={company.color}
                              fillOpacity={0.6}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  ))}
              </Slider>
            </Grid>
          </Grid>

          <Grid
            marginTop='50px'
            style={{ borderTop: `1px solid ${Colors.grey1}` }}
            sm={{ display: 'none' }}
          />
          <Grid marginTop='50px'>
            <Text bold size='1.3rem'>
              멘토별 피드백 통계
            </Text>
          </Grid>
          <Grid marginTop='5px'>
            <Text>멘토가 멘티들에게 평균적으로 주는 피드백과 내가 받은 피드백을 비교해 보세요.</Text>
          </Grid>

          <Grid justify='space-between' align='center' marginTop='30px'>
            <Grid
              isNoWrap
              padding='18px'
              gap='10px'
              justify='space-between'
              sm={{ padding: '8px', isNoWrap: true, gap: '5px' }}
            >
              <Grid isInline width='220px' sm={{ width: '80px' }} />
              <Grid isInline width='150px' sm={{ width: '85px' }} />
              <Grid isInline width='100px' justify='center' sm={{ width: '55px' }}>
                <Text size='0.9rem'>Overall</Text>
              </Grid>
              <Grid isInline width='100px' justify='center' sm={{ width: '55px' }}>
                <Text size='0.9rem'>Impact</Text>
              </Grid>
              <Grid isInline width='100px' justify='center' sm={{ width: '55px' }}>
                <Text size='0.9rem'>{screens.sm ? 'Problem Solving' : 'PS'}</Text>
              </Grid>
              <Grid isInline width='100px' justify='center' sm={{ width: '55px' }}>
                <Text size='0.9rem'>{screens.sm ? 'Communication' : 'Comm'}</Text>
              </Grid>
            </Grid>
            {!!mentorData &&
              (mentorData.length === 0 ? (
                <Grid
                  justify='center'
                  align='center'
                  height='100px'
                  margin='0 0 150px 0'
                  md={{ margin: '0 0 80px 0' }}
                >
                  <Spin spinning />
                </Grid>
              ) : (
                mentorData.map((data: TMentorFeedbackData, index) => {
                  const mentor = data.data;
                  return (
                    <div key={index} style={{ width: '100%' }}>
                      <Grid
                        isNoWrap
                        padding='18px'
                        gap='10px'
                        justify='space-between'
                        marginTop='12px'
                        border={`1px solid ${Colors.grey1}`}
                        sm={{ padding: '8px', isNoWrap: true, gap: '5px' }}
                      >
                        <Grid //프로필이미지 ~ 이름
                          isNoWrap
                          width='220px'
                          gap='15px'
                          align='center'
                          sm={{
                            width: '60px',
                            justify: 'center',
                            isNoWrap: false,
                          }}
                        >
                          <Grid //프로필
                            width='80px'
                            height='80px'
                            isInline
                            image={mentor.mentor?.profile_iamge || DefaultUser}
                            borderRadius='80px'
                            justify='center'
                            align='flex-end'
                            sm={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '44px',
                            }}
                          >
                            {mentor.mentor && (
                              <Grid
                                position='relative'
                                isInline
                                padding='1px 8px 0 8px'
                                borderRadius='9px'
                                backgroundColor={
                                  mentor.mentor?.mentor_type === 'senior' ? PrimaryColor : SecondaryColor
                                }
                                border={`3px solid ${Colors.white}`}
                                style={{ bottom: -10 }}
                                sm={{ padding: '1px 2px' }}
                              >
                                <Text color={Colors.white} size='0.8rem' sm={{ size: '0.6rem' }}>
                                  {mentor.mentor?.mentor_type === 'senior' ? '시니어' : '주니어'}
                                </Text>
                              </Grid>
                            )}
                          </Grid>
                          <Grid //이름
                            width='120px'
                            isInline
                            align='center'
                            sm={{ width: '100%' }}
                          >
                            <Grid justify='center'>
                              <Text size='1.8rem' bold ellipsis={1} sm={{ size: '1.2rem' }}>
                                {mentor.mentor?.fullname || '-'}
                              </Text>
                            </Grid>
                            <Grid justify='center' marginTop='10px' sm={{ marginTop: '5px' }}>
                              <Text size='0.9rem' align='center'>
                                {mentor.company_name || '-'}
                              </Text>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid //피드백 평균
                          width='150px'
                          isInline
                          align='center'
                          sm={{ width: '85px' }}
                        >
                          <Grid justify='center'>
                            <Text size='1.3rem' color={Colors.grey3} sm={{ size: '0.9rem', align: 'center' }}>
                              {screens.sm ? (
                                '피드백 평균'
                              ) : (
                                <>
                                  피드백
                                  <br />
                                  평균
                                </>
                              )}
                            </Text>
                          </Grid>
                          <Grid marginTop='25px' gap='5px' justify='center'>
                            <Text size='1.3rem' sm={{ size: '0.9rem' }}>
                              내 피드백
                            </Text>
                            <Text color={Colors.grey2} size='1.3rem' sm={{ size: '0.9rem' }}>
                              ({mentor.my_scores.feedback_count}개)
                            </Text>
                          </Grid>
                        </Grid>
                        <Grid width='100px' isInline align='center' sm={{ width: '55px' }}>
                          <Grid justify='center'>
                            <Text size='1.3rem' color={Colors.grey3} bold>
                              {mentor.mentor_scores.overall_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                          <Grid justify='center' marginTop='25px'>
                            <Text size='1.3rem' bold>
                              {mentor.my_scores.overall_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                        </Grid>
                        <Grid width='100px' isInline align='center' sm={{ width: '55px' }}>
                          <Grid justify='center'>
                            <Text size='1.3rem' color={Colors.grey3} bold>
                              {mentor.mentor_scores.impact_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                          <Grid justify='center' marginTop='25px'>
                            <Text size='1.3rem' bold>
                              {mentor.my_scores.impact_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                        </Grid>
                        <Grid width='100px' isInline align='center' sm={{ width: '55px' }}>
                          <Grid justify='center'>
                            <Text size='1.3rem' color={Colors.grey3} bold>
                              {mentor.mentor_scores.problemsolving_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                          <Grid justify='center' marginTop='25px'>
                            <Text size='1.3rem' bold>
                              {mentor.my_scores.problemsolving_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                        </Grid>
                        <Grid width='100px' isInline align='center' sm={{ width: '55px' }}>
                          <Grid justify='center'>
                            <Text size='1.3rem' color={Colors.grey3} bold>
                              {mentor.mentor_scores.communication_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                          <Grid justify='center' marginTop='25px'>
                            <Text size='1.3rem' bold>
                              {mentor.my_scores.communication_score?.toFixed(1) || '-'}
                            </Text>
                          </Grid>
                        </Grid>
                      </Grid>
                    </div>
                  );
                })
              ))}
          </Grid>
          {mentorDataTotal > mentorPage * mentorDataLimit && (
            <Grid justify='center' marginTop='50px'>
              <Button className='text' onClick={() => setMentorPage(mentorPage + 1)}>
                <Text size='1.3rem' textDecoration='underline'>
                  더보기
                </Text>
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </DashboardWrapper>
  );
};

const DashboardWrapper = styled.div`
  & .xAxis tspan {
    @media ${device.lg} {
      font-size: 9px;
    }
  }
  & tspan {
    @media ${device.lg} {
      font-size: 9px;
    }
  }
`;

export default Dashboard;
